// components/analytics/sections/WalletAnalytics.tsx
import { Row, Col, Card, Typography, Statistic, Tooltip } from "antd";
import { WalletOutlined, CheckCircleOutlined, ClockCircleOutlined, DollarOutlined, InfoCircleOutlined, BankOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { TrendLineChart } from "../components/charts/TrendLineChart";
import { PieChart } from "../components/charts/PieChart";
import type { WalletAnalytics as WalletAnalyticsType } from "../../../types/analytics";
import { useTheme } from "../../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface WalletAnalyticsProps {
  data?: WalletAnalyticsType | null;
  loading?: boolean;
}

// Componente ModernStatCard para WalletAnalytics
const ModernStatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string | React.ReactNode;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  info?: string;
  theme?: 'dark' | 'light';
}> = ({ title, value, icon, color, suffix, prefix, precision, loading, info, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px' 
      }}>
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ 
              color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
              fontSize: '12px' 
            }} />
          </Tooltip>
        )}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

// Componente para cards con información adicional
const InfoStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  theme?: 'dark' | 'light';
}> = ({ title, value, info, icon, color, prefix, precision, loading, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono e info */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text style={{ 
          fontSize: '13px', 
          fontWeight: 600, 
          color: theme === 'dark' ? '#d9d9d9' : '#595959', 
          margin: 0 
        }}>
          {title}
        </Text>
        <Tooltip title={info}>
          <InfoCircleOutlined style={{ 
            color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
            fontSize: '12px' 
          }} />
        </Tooltip>
      </div>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          boxShadow: `0 2px 8px ${color}40`
        }}
      >
        {icon}
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

const defaultData: WalletAnalyticsType = {
  transfers: {
    total: 0,
    validated: 0,
    pending: 0,
  },
  amounts: {
    total: 0,
    validated: 0,
    pending: 0,
  },
  platforms: [],
  volumeTrend: [],
  validationTrend: [],
};

export const WalletAnalytics: React.FC<WalletAnalyticsProps> = ({
  data = defaultData,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  console.log('[WalletAnalytics] data received:', JSON.stringify({ transfers: data?.transfers, amounts: data?.amounts }));

  // Calculate percentages with null safety
  const validatedPercent =
    data.transfers.total > 0
      ? (data.transfers.validated / data.transfers.total) * 100
      : 0;

  const pendingPercent =
    data.transfers.total > 0
      ? (data.transfers.pending / data.transfers.total) * 100
      : 0;

  // Safe check for empty platform data
  const hasPlatformData = data.platforms && data.platforms.length > 0;
  const hasVolumeTrend = data.volumeTrend && data.volumeTrend.length > 0;
  const hasValidationTrend =
    data.validationTrend && data.validationTrend.length > 0;

  return (
    <div>
      {/* Cards principales con diseño moderno */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.walletAnalytics.cards.totalTransfers")}
            value={data.transfers.total}
            icon={<WalletOutlined />}
            color="#fa8c16"
            loading={loading}
            info="Total de transferencias en el período"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.walletAnalytics.cards.validatedTransfers")}
            value={data.transfers.validated}
            icon={<CheckCircleOutlined />}
            color="#52c41a"
            suffix={`(${validatedPercent.toFixed(1)}%)`}
            loading={loading}
            info="Transferencias validadas exitosamente"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.walletAnalytics.cards.pendingValidation")}
            value={data.transfers.pending}
            icon={<ClockCircleOutlined />}
            color="#722ed1"
            suffix={`(${pendingPercent.toFixed(1)}%)`}
            loading={loading}
            info="Transferencias pendientes de validación"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.walletAnalytics.cards.totalAmount")}
            value={data.amounts.total}
            icon={<DollarOutlined />}
            color="#1890ff"
            prefix="$"
            precision={2}
            loading={loading}
            info="Monto total de todas las transferencias"
            theme={theme}
          />
        </Col>
      </Row>

      {/* Segunda fila de cards informativos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <InfoStatCard
            title="Monto Validado"
            value={data.amounts.validated}
            icon={<ThunderboltOutlined />}
            color="#13c2c2"
            prefix="$"
            precision={2}
            loading={loading}
            info="Monto total de transferencias validadas"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <InfoStatCard
            title="Monto Pendiente"
            value={data.amounts.pending}
            icon={<BankOutlined />}
            color="#eb2f96"
            prefix="$"
            precision={2}
            loading={loading}
            info="Monto total pendiente de validación"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <InfoStatCard
            title="Eficiencia de Validación"
            value={`${validatedPercent.toFixed(1)}%`}
            icon={<CheckCircleOutlined />}
            color="#faad14"
            loading={loading}
            info="Porcentaje de transferencias validadas vs total"
            theme={theme}
          />
        </Col>
      </Row>

      {/* Charts con diseño moderno */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WalletOutlined style={{ color: '#fa8c16' }} />
                {t("analytics.walletAnalytics.charts.platformDistribution.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {hasPlatformData ? (
              <PieChart 
                data={data.platforms}
                valueField="count"
                nameField="name"
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8c8c8c"
                }}
              >
                {t("analytics.walletAnalytics.charts.platformDistribution.noData")}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarOutlined style={{ color: '#1890ff' }} />
                {t("analytics.walletAnalytics.charts.volumeTrend.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {hasVolumeTrend ? (
              <TrendLineChart 
                data={data.volumeTrend.map(item => ({
                  date: item.date,
                  value: item.count,
                  type: 'Volume'
                }))}
                xField="date"
                yField="value"
                seriesField="type"
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8c8c8c"
                }}
              >
                {t("analytics.walletAnalytics.charts.volumeTrend.noData")}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                {t("analytics.walletAnalytics.charts.validationTrend.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {hasValidationTrend ? (
              <TrendLineChart
                data={data.validationTrend.flatMap(item => [
                    {
                      date: item.date,
                      value: item.validated,
                    type: 'Validated'
                    },
                    {
                      date: item.date,
                      value: item.pending,
                    type: 'Pending'
                  }
                ])}
                xField="date"
                yField="value"
                seriesField="type"
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8c8c8c"
                }}
              >
                {t("analytics.walletAnalytics.charts.validationTrend.noData")}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
